import { progressPct } from "@/lib/progress";
import { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { statusMeta, phaseColor, HEALTH_COLORS, HEALTH_LABELS, STATUSES } from "@/lib/constants";

export const CHART_DEFS = [
  { key: "health", label: "Health mix" },
  { key: "status", label: "Status breakdown" },
  { key: "phase", label: "Avg % complete by phase" },
  { key: "actions", label: "Action items progress" },
  { key: "risks", label: "Open risks by severity" },
];

export function ReportChart({ kind, tasks = [], cpTasks = [], risks = [], actions = [], phaseColors, height = 200, progressStyle }) {
  const data = useMemo(() => {
    if (kind === "health") {
      const counts = {};
      cpTasks.forEach((t) => { counts[t.health] = (counts[t.health] || 0) + 1; });
      return ["blue", "green", "yellow", "red", "gray"]
        .map((k) => ({ key: k, name: HEALTH_LABELS[k], value: counts[k] || 0, color: HEALTH_COLORS[k] }))
        .filter((d) => d.value > 0);
    }
    if (kind === "status") {
      return STATUSES.map((s) => ({ name: s.label, value: tasks.filter((t) => !t.is_summary && t.status === s.key).length, color: s.color }));
    }
    if (kind === "phase") {
      const map = {};
      (cpTasks.length ? cpTasks : tasks).forEach((t) => {
        const p = t.phase || "Unphased";
        if (!map[p]) map[p] = { phase: p, items: [] };
        map[p].items.push(t);
      });
      return Object.values(map).map((m) => ({
        name: m.phase.length > 14 ? m.phase.slice(0, 13) + "…" : m.phase,
        value: progressPct(m.items, progressStyle),
        color: (phaseColors && phaseColors[m.phase]) || phaseColor(m.phase),
      }));
    }
    if (kind === "actions") {
      const b = { late: 0, pending: 0, completed: 0 };
      actions.filter((a) => !a.archived).forEach((a) => { b[a.bucket || "pending"] += 1; });
      return [
        { key: "late", name: "Late", value: b.late, color: "#EF4444" },
        { key: "pending", name: "Pending", value: b.pending, color: "#10B981" },
        { key: "completed", name: "Completed", value: b.completed, color: "#2563EB" },
      ].filter((d) => d.value > 0);
    }
    if (kind === "risks") {
      const sev = { Low: 0, Moderate: 0, High: 0, Critical: 0 };
      const col = { Low: "#10B981", Moderate: "#F59E0B", High: "#F97316", Critical: "#EF4444" };
      risks.filter((r) => r.status !== "closed").forEach((r) => {
        const s = r.score || 0;
        const k = s >= 15 ? "Critical" : s >= 10 ? "High" : s >= 5 ? "Moderate" : "Low";
        sev[k] += 1;
      });
      return Object.entries(sev).map(([name, value]) => ({ name, value, color: col[name] })).filter((d) => d.value > 0);
    }
    return [];
  }, [kind, tasks, cpTasks, risks, actions, phaseColors, progressStyle]);

  const def = CHART_DEFS.find((d) => d.key === kind);
  const isPie = kind === "health" || kind === "actions";

  return (
    <div className="border border-border rounded-lg p-4 break-inside-avoid" data-testid={`report-chart-${kind}`}>
      <h4 className="font-display font-bold text-sm mb-2">{def?.label || kind}</h4>
      {data.length === 0 ? (
        <p className="text-xs text-muted-foreground py-14 text-center">No data yet.</p>
      ) : isPie ? (
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" innerRadius={height * 0.22} outerRadius={height * 0.37} paddingAngle={2} strokeWidth={0} isAnimationActive={false}>
              {data.map((d, i) => <Cell key={`${d.name}-${i}`} fill={d.color} />)}
            </Pie>
            <Tooltip />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer>
      ) : kind === "phase" ? (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 20, bottom: 0, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f7" />
            <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
            <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 10 }} />
            <Tooltip cursor={{ fill: "rgba(148,163,184,0.08)" }} formatter={(v) => `${v}%`} />
            <Bar dataKey="value" radius={[0, 6, 6, 0]} isAnimationActive={false}>
              {data.map((d, i) => <Cell key={`${d.name}-${i}`} fill={d.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
            <Tooltip cursor={{ fill: "rgba(148,163,184,0.08)" }} />
            <Bar dataKey="value" radius={[6, 6, 0, 0]} isAnimationActive={false}>
              {data.map((d, i) => <Cell key={`${d.name}-${i}`} fill={d.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
